import { useMemo, useRef, useState, type ComponentProps } from 'react';
import { teamFilter } from '../use-teams';
import {
  wireMonday,
  addWireDays,
  formatHours,
  type Worklog,
  type WorklogDayTotals,
  type WorklogRules,
} from '@forecastify/shared';
import { entryLock } from './entry-lock';
import {
  Button,
  Icon,
  RangePicker,
  DataGrid,
  DataList,
  ListPager,
  ListToolbar,
  SKELETON_WIDTHS,
  Skeleton,
  Timesheet,
  skeletonWidth,
  type DataGridColumn,
  type DataGridRow,
  type MenuItem,
  type PagerState,
  type TimesheetCol,
  type TimesheetRow,
  type SortState,
  LandscapeOnly,
  usePortraitPhone,
} from '@forecastify/ui';
import { useI18n, useT, type Translate } from '../../i18n';
import { useCalendarLabels } from '../date-field';
import { listLabels } from '../delivery/clients-screen';
import { CellPopover } from './cell-popover';
import { LogsCalendar, type CalendarRange } from './logs-calendar';
import { dowLabel } from './week';
import { MY_WORKLOG_COLUMNS, worklogColumns, worklogRows } from './worklog-columns';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE FOUR LOG SURFACES — one toolbar, one filter set, four bodies.       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * C12. `logViewSwitch` is the mockup's own list, in its order: **Lista, Timesheet, Naptár, Rács.**
 * The same arrangement C9 gave the four task views, for the same reason: four screens with four
 * search boxes is four places for a filter to behave differently, and switching view would reset
 * what somebody had narrowed to.
 *
 * ⚠️ **The four disagree about what they can show, and saying so is the honest part:**
 *
 *  * **Lista** and **Rács** show the loaded page of whatever the filters select, newest first;
 *  * **Timesheet** and **Naptár** are *one week*, so they ignore the range filter and read the week
 *    they are pointed at. A week view that silently obeyed a "this month" range would draw a
 *    seven-day window over thirty days of data and look simply wrong.
 *
 * ⚠️ **Import and export are C17's**, so the mockup's `ioActionsTime` and its CSV bulk action are
 * not here. A button that toasts *"export ready"* over nothing is worse than no button.
 */

export type LogView = 'list' | 'timesheet' | 'calendar' | 'grid';

const VIEWS: readonly {
  value: LogView;
  icon: 'list-checks' | 'calendar-days' | 'calendar' | 'layout-grid';
}[] = [
  { value: 'list', icon: 'list-checks' },
  { value: 'timesheet', icon: 'calendar-days' },
  { value: 'calendar', icon: 'calendar' },
  { value: 'grid', icon: 'layout-grid' },
];

function viewLabel(t: Translate, view: LogView): string {
  if (view === 'list') return t('logs.view.list');
  if (view === 'timesheet') return t('logs.view.timesheet');
  if (view === 'calendar') return t('logs.view.calendar');
  return t('logs.view.grid');
}

export interface LogsScreenProps {
  /**
   * ┌────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ WHOSE HOURS — and it decides three things, not one.                   │
   * └────────────────────────────────────────────────────────────────┘
   *
   * ⚠️⚠️ **ONE SCREEN AND TWO ADDRESSES SERVED IT, WITH ONE SCOPE BETWEEN THEM.** *Időrögzítés
   * → Időlogok* and *Workspace → Időlogok* both landed on everybody's hours, so the personal
   * section answered a question nobody asked it: the reader opened their own time section and read
   * a page of other people's names. Reported 20 Sep.
   *
   * ⚠️ **`requests-screen.tsx` is the precedent** — `listScope` there separates *Sáját
   * kérelmeim* from the queue over the same body, down to the `data-screen` marker.
   *
   * ⚠️⚠️ **IT IS A PRESET, NOT A PERMISSION.** The server narrows every read to the caller's
   * `worklog.read` scope, so somebody holding `own` sees only their own hours on BOTH addresses
   * and has lost nothing. This chooses which question the screen asks, never what it may see.
   */
  readonly listScope: 'mine' | 'all';
  /** The loaded page, newest day first. */
  readonly entries: readonly Worklog[];
  /** The week the timesheet and the calendar are pointed at — a Monday. */
  readonly weekStart: string;
  /**
   * That week's entries — of the tasks on the week's current page. A separate read, because a
   * week is not the list's page; 31 Oct 2026 it pages by task (`logs-route.tsx`).
   */
  readonly weekEntries: readonly Worklog[];
  /**
   * 31 Oct 2026 · the whole week's hours per day, for the column and week totals — which the
   * entries above cannot give once they are one page of tasks. Absent or `null`, the totals are
   * added up from `weekEntries`.
   */
  readonly weekTotals?: WorklogDayTotals | null;
  /** The week's pager, over tasks. Absent while the week fits one page. */
  readonly weekPage?: PagerState | undefined;
  readonly today: string;
  readonly projects: readonly { id: string; name: string }[];
  readonly members: readonly { id: string; name: string }[];
  readonly memberId: string;
  readonly onMember: (value: string) => void;
  /** R-60 · the team filter, on the workspace list. Absent on an embedded copy. */
  readonly teams?: readonly { readonly id: string; readonly name: string }[];
  readonly teamId?: string;
  readonly onTeam?: (value: string) => void;
  /** Open tasks the timesheet's add-row control can offer. */
  readonly addableTasks: readonly { id: string; key: string; title: string; closed?: boolean }[];
  readonly search: string;
  /** 17 Oct 2026 · the contract a period's row menu narrowed the list to, with its own chip. */
  readonly contract?: { readonly id: string; readonly label: string } | null;
  readonly onClearContract?: () => void;
  readonly projectId: string;
  readonly range: 'all' | 'week' | 'month';
  /**
   * ⚠️⚠️ **A PAGER SINCE ITEM 38, WHERE `hasMore` / `isLoadingMore` / `onLoadMore` USED TO BE.**
   * This list appended — page two arrived under page one and the only way back was a reload — while
   * every other list in the product had moved to `[<] [selector] [>] [total]` in item 20.
   * `state-rules.md` §6's *"consistent within a screen"* had quietly become *"except here"*.
   */
  readonly page: ComponentProps<typeof DataList>['page'];
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  /** ⚠️ Controlled, and the SERVER orders — `DataList` never reorders what it is handed. */
  readonly sort: readonly SortState[];
  readonly onSortChange: (next: readonly SortState[]) => void;
  readonly canLog: boolean;
  readonly canEdit: boolean;
  /** 30 Oct 2026 · C13's rules and `worklog.lock` — why an entry cannot be changed (`entryLock`). */
  readonly rules: WorklogRules;
  readonly mayOverride: boolean;
  readonly serverError: string | null;
  readonly onSearch: (value: string) => void;
  readonly onProject: (value: string) => void;
  readonly onRange: (value: 'all' | 'week' | 'month') => void;
  readonly onShiftWeek: (weeks: number) => void;
  readonly onOpenTask: (entry: Worklog) => void;
  readonly onDuplicate: (entry: Worklog) => void;
  readonly onDelete: (entry: Worklog) => void;
  /** C48 · which of these hours already carry an overtime claim. See the row menu. */
  readonly overtimeClaimed: ReadonlySet<string>;
  readonly onMarkOvertime: (entry: Worklog) => void;
  /* ⚠️ T-10's shared paths — this screen holds neither a CSV writer nor a parser. */
  readonly onExport: () => void;
  readonly onImport: () => void;
  readonly onDeleteMany: (ids: readonly string[]) => void;
  /** The grid's single-cell edit, and the timesheet's. Both send the raw string. */
  readonly onEditHours: (entry: Worklog, hours: string) => void;
  readonly onEditNote: (entry: Worklog, note: string) => void;
  readonly onEditDay: (entry: Worklog, day: string) => void;
  /** The timesheet's empty cell: a task, a day and a raw amount. */
  /**
   * ⚠️ **The note is optional and round 5 item 32 is why it exists.** The range tools and the
   * grid's own cells log hours and nothing else — there is nowhere to type a note in a 92px cell.
   * The cell modal has a field per row, so a second entry on a day can say what it was for, which
   * is the only thing that tells two entries on one task-day apart.
   */
  readonly onLogAt: (taskId: string, day: string, hours: string, note?: string) => void;
  readonly onAddRow: (taskId: string) => void;
  /**
   * 30 Oct 2026 · the add-row menu's search, answered on the server: `addableTasks` is what
   * matched (and the rows already added), never one page of the workspace's tasks.
   */
  readonly onAddableQuery: (query: string) => void;
  readonly onCreateRange: (range: CalendarRange) => void;
  readonly onOpenEntry: (entry: Worklog) => void;
  /**
   * ⚠️⚠️ **ITEM 17'S RANGE, AS ONE CALL.** The timesheet asks for *"this row's hours on these
   * days"*; the route decides what that means per day — an existing entry is edited, an empty day
   * gets a new one, and `0` deletes. One call rather than a write per column, because a week of
   * eight separate refusals is eight error strips for one gesture.
   */
  readonly onSetRange: (taskKey: string, days: readonly string[], hours: string) => void;

  readonly onBlocked: (reason: string) => void;
  /** Rows the timesheet shows because somebody added them, with nothing logged yet. */
  readonly extraTaskIds: readonly string[];
  readonly onRemoveRow: (taskId: string) => void;
}

export function LogsScreen(props: LogsScreenProps) {
  const t = useT();
  const { locale } = useI18n();
  const [view, setView] = useState<LogView>('list');

  /**
   * ⚠️⚠️ **WHICH CELL IS OPEN — round 5 item 32.** A task key and an ISO day, which is what the
   * grid calls a cell and all the modal needs: the entries are found in `weekEntries`, which the
   * route already holds and re-reads after every write, so the modal follows a delete or an edit
   * without a fetch of its own and cannot show a stale row beside a fresh one.
   */
  const [openCell, setOpenCell] = useState<{ rowKey: string; iso: string } | null>(null);
  /**
   * ⚠️ **A ref rather than state, because it is an ELEMENT and not a value.** `useAnchorRect`
   * takes one and measures it on layout; putting a DOM node in state would re-render the screen
   * every time a different cell is opened, for a node nothing renders from.
   */
  const cellAnchor = useRef<HTMLElement | null>(null);

  const {
    entries,
    weekStart,
    weekEntries,
    weekTotals,
    weekPage,
    today,
    projects,
    addableTasks,
    search,
    projectId,
    range,
    page,
    sort,
    onSortChange,
    canLog,
    canEdit,
    serverError,
  } = props;
  const loading = props.loading === true;

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addWireDays(weekStart, index) ?? weekStart),
    [weekStart],
  );

  /* ── Lista ────────────────────────────────────────────────────────────────────────────── */

  /* ⚠️ **The mapping is `worklog-columns.ts`' now**, shared with the agent's reply card — see that
   * file's header, and `task-columns.ts` for the same argument at greater length. */
  const listRows = worklogRows(t, entries);
  /* ⚠️ No *Felelős* column on my own hours — `MY_WORKLOG_COLUMNS` argues it. */
  const listColumns = worklogColumns(
    t,
    props.listScope === 'mine' ? MY_WORKLOG_COLUMNS : undefined,
  );

  /* ── Rács ─────────────────────────────────────────────────────────────────────────────── */

  /** The mockup's `LOG_COLS`, in its order and with its widths. */
  const gridColumns: DataGridColumn[] = [
    { key: 'day', label: t('time.column.date'), width: 120, mono: true, required: true },
    { key: 'task', label: t('time.column.task'), width: 120, mono: true, readOnly: true },
    { key: 'project', label: t('projects.column.client'), width: 180, readOnly: true },
    { key: 'hours', label: t('time.column.hours'), width: 90, mono: true, required: true },
    { key: 'note', label: t('time.column.note'), width: 320 },
  ];

  /**
   * ⚠️ **The task column is READ-ONLY here, and the mockup's is editable.** Its grid takes a task
   * *key* as free text; ours would need a uuid, and a grid cell is the wrong control for picking one
   * — a typo'd key would be a silent `not_found` on save. Moving an entry to another task is the
   * dialog's job, where the picker is a picker. Recorded rather than transcribed.
   */
  const gridRows: DataGridRow[] = entries.map((entry) => ({
    id: entry.id,
    day: entry.workedOn,
    task: entry.taskKey,
    project: entry.projectName ?? t('time.noProject'),
    hours: formatHours(entry.hours),
    note: entry.note ?? '',
  }));

  /* ── Timesheet ────────────────────────────────────────────────────────────────────────── */

  /**
   * ⚠️ Item 42 · `useCalendarLabels` is the app's one translated calendar vocabulary — `date-field.tsx`
   * records why twenty call sites must not each build their own.
   */
  const calendar = useCalendarLabels();
  const rangeLabels = {
    ...calendar,
    apply: t('common.apply'),
    reset: t('common.reset'),
    trigger: t('time.week.range'),
    dialogLabel: t('time.week.range'),
  };

  const timesheet = useMemo(() => {
    /* ⚠️ The server's totals when there are any: a page of tasks is not the week, and a column
       added up from it would understate every day the next page also logged. */
    const dayHours =
      weekTotals === null || weekTotals === undefined
        ? null
        : new Map(weekTotals.days.map((entry) => [entry.day, Number(entry.hours)]));
    const cols: TimesheetCol[] = days.map((day, index) => {
      const total =
        dayHours === null
          ? weekEntries
              .filter((entry) => entry.workedOn === day)
              .reduce((sum, entry) => sum + Number(entry.hours), 0)
          : (dayHours.get(day) ?? 0);

      return {
        d: day,
        dow: dowLabel(t, index),
        dom: `${String(Number(day.slice(8)))}.`,
        label: day,
        weekend: index > 4,
        today: day === today,
        total: formatHours(total),
        /* ⚠️ Over eight hours in a day is flagged, not refused. C13 owns the real rule; this is the
         * mockup's own `overCapacity: total > 8`, and a flag is all the contract carries. */
        overCapacity: total > 8,
      };
    });

    /** Every task with hours this week, plus the rows somebody added by hand. */
    const taskIds = [
      ...new Set([...weekEntries.map((entry) => entry.taskId), ...props.extraTaskIds]),
    ];

    const rows: TimesheetRow[] = taskIds.map((taskId) => {
      const mine = weekEntries.filter((entry) => entry.taskId === taskId);
      const first = mine[0];
      const added = addableTasks.find((task) => task.id === taskId);

      const cells: TimesheetRow['cells'] = {};
      for (const day of days) {
        const allocs = mine.filter((entry) => entry.workedOn === day);
        const hours = allocs.reduce((sum, entry) => sum + Number(entry.hours), 0);
        cells[day] = {
          hours,
          label: formatHours(hours),
          allocs: allocs.map((entry) => ({
            id: entry.id,
            label: formatHours(entry.hours),
            ...(entry.note === null ? {} : { tag: entry.note }),
          })),
        };
      }

      const total = mine.reduce((sum, entry) => sum + Number(entry.hours), 0);

      return {
        k: first?.taskKey ?? added?.key ?? taskId,
        title: first?.taskTitle ?? added?.title ?? '',
        ...(first?.projectColor === null || first?.projectColor === undefined
          ? {}
          : { color: first.projectColor }),
        cells,
        total: formatHours(total),
        /**
         * ⚠️ **Item 39 · the row's title opens its task.** Only a row with a LOGGED entry can:
         * `onOpenTask` takes a worklog, which is how every other route in this screen reaches a
         * task, and a row somebody has just added has none yet.
         */
        ...(first === undefined
          ? {}
          : {
              onOpen: () => {
                props.onOpenTask(first);
              },
            }),
        /* ⚠️ Removable only while nothing is logged against it — the contract's own rule, and
         * removing a row with hours in it would imply deleting them. */
        ...(total === 0
          ? {
              onRemove: () => {
                props.onRemoveRow(taskId);
              },
            }
          : {}),
      };
    });

    const weekTotal =
      weekTotals === null || weekTotals === undefined
        ? weekEntries.reduce((sum, entry) => sum + Number(entry.hours), 0)
        : Number(weekTotals.hours);
    return { cols, rows, weekTotal: formatHours(weekTotal), taskIds };
  }, [days, weekEntries, weekTotals, today, t, addableTasks, props]);

  const taskOptions: MenuItem[] = [
    { type: 'label', label: t('logs.timesheet.addTask') },
    ...addableTasks
      .filter((task) => !timesheet.taskIds.includes(task.id))
      .map((task) =>
        task.closed === true
          ? {
              label: `${task.key} · ${task.title}`,
              disabled: true,
              description: t('logs.timesheet.taskClosed'),
            }
          : {
              label: `${task.key} · ${task.title}`,
              onSelect: () => {
                props.onAddRow(task.id);
              },
            },
      ),
  ];

  /**
   * ┌────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ WHICH WEEK — IN THE TOP ROW, WITH ARROWS. Feedback round 4 item 18.       │
   * └────────────────────────────────────────────────────────────────────┘
   *
   * Three things the round asked for, and they are one control:
   *
   * — **In the top row.** It sat in a second bar under the toolbar, so the week surfaces carried
   *   two rows of chrome and the one saying *which week* was the lower of the two.
   * — **Say the current week.** It read `2026-08-17 – 2026-08-23`: two wire dates, the format the
   *   server speaks rather than one anybody reads. It says the month and the days now, and offers
   *   a way back to the week somebody is actually in.
   * — **Arrows to step weeks.** Item 42 replaced a two-item menu with the range picker and took
   *   the stepping with it — right for *"a week three months back"*, wrong for *"last week"*,
   *   which is the overwhelmingly common move. Both now: arrows for the step, the picker for the
   *   jump.
   *
   * ⚠️ **BOTH week surfaces**, not just the timesheet — the calendar reads a week too, and item 42
   * learned the hard way that removing its stepper leaves it with no way to change week at all.
   */
  const weekEnd = addWireDays(weekStart, 6) ?? weekStart;
  const thisWeek = wireMonday(today) === weekStart;

  /** ⚠️ Whole weeks, from the two Mondays — `onShiftWeek` is the one path that reloads the data. */
  const goToWeekOf = (day: string): void => {
    const from = wireMonday(day);
    const current = wireMonday(weekStart);
    if (from === null || current === null || from === current) return;
    const weeks = Math.round(
      (Date.parse(`${from}T00:00:00Z`) - Date.parse(`${current}T00:00:00Z`)) /
        (7 * 24 * 60 * 60 * 1000),
    );
    if (weeks !== 0) props.onShiftWeek(weeks);
  };

  const weekControl = (
    <div className="flex shrink-0 items-center gap-1">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={t('logs.week.previous')}
        onClick={() => {
          props.onShiftWeek(-1);
        }}
      >
        <Icon name="chevron-left" size={15} />
      </Button>

      {/**
       * ⚠️⚠️ **THE TIMESHEET IS STILL A WEEK, AND THE PICKER SNAPS TO ONE** — item 42's rule,
       * kept. Its seven columns are built from `weekStart`, and a fortnight of them would be a
       * different component, so what this picks is *"a week containing this day"*. Offering a free
       * range and then quietly rounding it would be worse than offering a week.
       *
       * ⚠️ **`twoUp`**: two months side by side, so a range crossing a month boundary is one
       * glance rather than a month step in the middle of a selection.
       */}
      <RangePicker
        twoUp
        today={today}
        value={{ from: weekStart, to: weekEnd }}
        labels={rangeLabels}
        /* ⚠️ The button says the week the way somebody would — the picker prints two wire dates
           otherwise, which is what the round objected to. */
        format={(range) => weekLabel(range.from, range.to, locale)}
        onChange={(next) => {
          goToWeekOf(next.from);
        }}
      />

      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={t('logs.week.next')}
        onClick={() => {
          props.onShiftWeek(1);
        }}
      >
        <Icon name="chevron-right" size={15} />
      </Button>

      {/* ⚠️ Only when it is NOT this week: a button that does nothing is furniture, and its
          absence is itself the answer to *"am I looking at this week?"* */}
      {thisWeek ? null : (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            goToWeekOf(today);
          }}
        >
          {t('logs.week.thisWeek')}
        </Button>
      )}
    </div>
  );

  /* ── the shared toolbar ───────────────────────────────────────────────────────────────── */

  /**
   * ⚠️ **The range filter is hidden on the two week views rather than disabled.** They read a week
   * regardless, so leaving it visible would offer a control that does nothing — and a disabled one
   * would be a promise about a near future nothing here can keep.
   */
  const weekly = view === 'timesheet' || view === 'calendar';
  const portrait = usePortraitPhone();

  /**
   * ⚠️⚠️ **ONE TOOLBAR, AND THE VIEW DECIDES WHO DRAWS IT.** `markup.html` 653 gives the log
   * `DataList` the search, the filters and the actions, and renders no toolbar above it. Doing both
   * stacks two search boxes — which is what shipped here, and what was reported.
   */
  const toolbar: ComponentProps<typeof ListToolbar> = {
    search: search,
    onSearchChange: props.onSearch,
    filters: [
      ...(props.contract === undefined || props.contract === null
        ? []
        : [
            {
              key: 'contract',
              label: t('logs.filter.contract'),
              value: props.contract.id,
              onChange: () => {
                props.onClearContract?.();
              },
              options: [{ value: props.contract.id, label: props.contract.label }],
            },
          ]),
      /* ⚠️ `task.field.project`, where this said *Ügyfél*: it filters by PROJECT, and the chip
         repeated the wrong word back at whoever set it. The same mislabel was on the tasks list. */
      {
        key: 'project',
        label: t('task.field.project'),
        value: projectId,
        onChange: props.onProject,
        options: [
          { value: '', label: t('common.all') },
          ...projects.map((project) => ({ value: project.id, label: project.name })),
        ],
      },
      /* ⚠️⚠️ **AND NO *WHO* FILTER ON MY OWN HOURS.** A picker whose only honest answer is
         the reader themselves is a control that cannot change the list — the same argument as the
         column, one row up. The workspace list keeps it; it is the point of that list. */
      ...(props.listScope === 'mine'
        ? []
        : [
            {
              key: 'member',
              label: t('project.tasks.column.assignee'),
              value: props.memberId,
              onChange: props.onMember,
              options: [
                { value: '', label: t('common.all') },
                ...props.members.map((member) => ({ value: member.id, label: member.name })),
              ],
            },
            ...(props.onTeam === undefined
              ? []
              : teamFilter(t, props.teams ?? [], props.teamId ?? '', props.onTeam)),
          ]),
    ],
    ...(weekly
      ? {}
      : {
          views: [
            { value: 'all', label: t('logs.range.all') },
            { value: 'week', label: t('logs.range.week') },
            { value: 'month', label: t('logs.range.month') },
          ],
          view: range,
          onViewChange: (value: string) => {
            props.onRange(value === 'week' || value === 'month' ? value : 'all');
          },
        }),
    viewSwitch: {
      value: view,
      onChange: (value) => {
        setView(value as LogView);
      },
      items: VIEWS.map((entry) => ({
        value: entry.value,
        label: viewLabel(t, entry.value),
        icon: entry.icon,
      })),
    },
    /**
     * ⚠️⚠️ **THE IMPORT AND EXPORT ARROWS THE MOCKUP PUTS ON THIS LIST**, which C11 left as a note
     * saying *"`ioActionsTime` and the CSV bulk action are C17's"*. Both open a shared dialog.
     *
     * ⚠️ Always present, unlike the week navigator — a list you can export is a list you can
     * export whichever view is showing.
     */
    actions: [
      {
        label: t('io.actions'),
        icon: 'database' as const,
        items: [
          { label: t('export.run'), icon: 'download' as const, onSelect: props.onExport },
          { label: t('import.run'), icon: 'upload' as const, onSelect: props.onImport },
        ],
      },
      /*
        ⚠️⚠️ **THE WEEK STEPPER IS GONE — item 42 asked for a dual-calendar range picker.** It was
        a menu with *Előző hét* and *Következő hét* in it, so reaching a week three months back was
        twelve clicks through a menu that closed after each one. The picker is rendered beside the
        toolbar rather than inside it, because a `ToolbarAction` is a button or a menu and this is
        neither.
      */
    ],
    labels: { ...listLabels(t), search: t('logs.search') },
  };

  const rowMenu = (id: string): MenuItem[] => {
    const entry = entries.find((candidate) => candidate.id === id);
    if (entry === undefined) return [];
    return [
      {
        label: t('logs.row.openTask'),
        icon: 'arrow-right',
        onSelect: () => {
          props.onOpenTask(entry);
        },
      },
      ...(canLog
        ? [
            {
              label: t('logs.row.duplicate'),
              icon: 'copy' as const,
              onSelect: () => {
                props.onDuplicate(entry);
              },
            },
          ]
        : []),
      /**
       * ⚠️⚠️ **C48 · THE ONLY WAY OVERTIME IS EVER RAISED.** The mockup's own empty state on
       * *Kérelmeim* is the specification: *"túlórát az időrögzítésnél jelölöd — a bejegyzésből
       * automatikusan kérelem lesz"*. There is no form that takes an hour count anywhere in the
       * product, and this menu item is why.
       *
       * ⚠️ **Disabled once the hour is claimed, saying so** (30 Oct 2026 — it was absent, which
       * left people hunting for it). The route holds the live claims' ids, so the menu can tell —
       * and a withdrawn claim is not one of them: the hour can be claimed again.
       */
      ...(canLog
        ? [
            props.overtimeClaimed.has(entry.id)
              ? {
                  label: t('logs.row.markOvertime'),
                  icon: 'timer' as const,
                  disabled: true,
                  description: t('logs.row.markOvertimeClaimed'),
                }
              : {
                  label: t('logs.row.markOvertime'),
                  icon: 'timer' as const,
                  onSelect: () => {
                    props.onMarkOvertime(entry);
                  },
                },
          ]
        : []),
      ...(canEdit
        ? [
            { type: 'separator' as const },
            /* 30 Oct 2026 · refused by a locked month or an approved overtime claim — drawn
               disabled and saying which, as the timesheet's own trash does. */
            ((): MenuItem => {
              const refused =
                entry.overtime?.status === 'approved'
                  ? t('time.error.overtimeApproved')
                  : entryLock(t, entry.workedOn, props.rules, props.mayOverride).remove;
              return refused === null
                ? {
                    label: t('common.delete'),
                    icon: 'trash',
                    destructive: true,
                    onSelect: () => {
                      props.onDelete(entry);
                    },
                  }
                : {
                    label: t('common.delete'),
                    icon: 'trash',
                    destructive: true,
                    disabled: true,
                    description: refused,
                  };
            })(),
          ]
        : []),
    ];
  };

  return (
    /* ⚠️ Its OWN marker per scope, as the request lists do: the reachability gate asserts an
       address renders its own screen, and one marker for two addresses would pass for both. */
    <div
      data-screen={props.listScope === 'mine' ? 'time-logs' : 'workspace-logs'}
      className="flex min-h-0 flex-1 flex-col"
    >
      {serverError !== null && (
        <p
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive m-3 rounded-md border px-3 py-2 text-[12.5px]"
        >
          {serverError}
        </p>
      )}

      {/* ⚠️ Not on the list view — its `DataList` draws this same set itself. */}
      {view === 'list' ? null : (
        <ListToolbar
          {...toolbar}
          {...(weekly ? { lead: weekControl } : {})}
          /* ⚠️⚠️ **THE VIEW SWITCH IS THIS SCREEN'S MAIN ACTION, so item 26 puts it on the right.**
             You cannot create a worklog from this toolbar — *Adatok* exports and imports, and that
             is not what anybody came for. `list-toolbar.tsx` argues the prop. */
          viewSwitchPrimary
        />
      )}

      {view === 'list' && (
        <div className="min-h-0 flex-1">
          <DataList
            columnsKey="logs"
            card={{
              title: 'task',
              context: 'date',
              defaults: { avatar: 'member', details: ['hours', 'project', 'note'] },
            }}
            {...toolbar}
            /* ⚠️ The list view draws its own toolbar, so the rule has to reach it too — see the
               note on the `ListToolbar` above. */
            viewSwitchPrimary
            columns={listColumns}
            rows={listRows}
            isLoading={loading}
            rowMenu={rowMenu}
            bulkActions={
              canEdit
                ? [
                    {
                      label: t('common.delete'),
                      icon: 'trash',
                      destructive: true,
                      onSelect: (ids) => {
                        props.onDeleteMany(ids);
                      },
                    },
                  ]
                : undefined
            }
            empty={{
              icon: 'clock',
              title: t('logs.empty'),
              body: t('logs.emptyNote'),
            }}
            {...(page === undefined ? {} : { page })}
            sort={sort}
            onSortChange={onSortChange}
            /* ⚠️ `logs.search` is this screen's own placeholder, and a bare `listLabels(t)` here
               silently puts the generic wording back — it did, because this prop comes AFTER the
               spread above and wins. */
            labels={{ ...listLabels(t), search: t('logs.search') }}
          />
        </div>
      )}

      {/* 17 Oct 2026 · the timesheet reads only sideways on a phone — the product owner. */}
      {view === 'timesheet' && portrait ? (
        <LandscapeOnly message={t('mobile.rotate')}>{null}</LandscapeOnly>
      ) : null}

      {view === 'timesheet' && !portrait && loading && <TimesheetSkeleton />}

      {view === 'timesheet' && !portrait && !loading && (
        <Timesheet
          cols={timesheet.cols}
          rows={timesheet.rows}
          weekTotal={timesheet.weekTotal}
          taskOptions={canLog ? taskOptions : undefined}
          onTaskQuery={props.onAddableQuery}
          onSet={(taskKeyOrId, iso, value) => {
            /* ⚠️ The row key is the TASK KEY, because that is what the contract names a row by. The
             * write needs the id, so it is resolved back here rather than in the component. */
            const existing = weekEntries.find(
              (entry) => entry.taskKey === taskKeyOrId && entry.workedOn === iso,
            );
            if (existing !== undefined) {
              props.onEditHours(existing, value);
              return;
            }
            const task =
              weekEntries.find((entry) => entry.taskKey === taskKeyOrId) ??
              addableTasks.find((candidate) => candidate.key === taskKeyOrId);
            const taskId = task === undefined ? '' : 'taskId' in task ? task.taskId : task.id;
            if (taskId !== '') props.onLogAt(taskId, iso, value);
          }}
          onDelete={(allocId) => {
            const entry = weekEntries.find((candidate) => candidate.id === allocId);
            if (entry !== undefined) props.onDelete(entry);
          }}
          /* ⚠️ A cell holding two entries shows their SUM and cannot be typed into — see the note
           * on the component. This is where those entries are actually edited. */
          onOpenAlloc={(allocId) => {
            const entry = weekEntries.find((candidate) => candidate.id === allocId);
            if (entry !== undefined) props.onOpenEntry(entry);
          }}
          /**
           * ⚠️⚠️ **ITEM 17 · COPY A CELL ONWARDS, OR CLEAR A SPAN** — the gesture the resource
           * planner has had since C41, on the grid where *"the same eight hours, Monday to Friday"*
           * is the commonest thing anybody types. `timesheet.tsx` argues the interaction; what
           * arrives here is a task, some days and the hours to put on each.
           */
          {...(canLog
            ? {
                onRange: (taskKey: string, days: readonly string[], hours: number) => {
                  props.onSetRange(taskKey, days, hours === 0 ? '0' : formatHours(hours));
                },
              }
            : {})}
          /* ⚠️⚠️ **ITEM 32 — the cell's own entries, in the planner's pop-over.** It hands
             back the element it was clicked on, because that is what the pop-over hangs off;
             `timesheet.tsx` argues why the cell stopped being an input to do it. */
          onOpenCell={(rowKey, iso, anchor) => {
            cellAnchor.current = anchor;
            setOpenCell({ rowKey, iso });
          }}
          /**
           * ⚠️⚠️ **THE CELL MENU IS GONE, AND ITS ONE ITEM IS IN THE POP-OVER — item 32.**
           * *Mark as overtime* used to be the cell's whole dropdown, and it applied to **every
           * unclaimed entry in the cell**, because a menu hanging off a cell has no way to name
           * one of them. P3-5 puts the claim on the HOUR: a morning session and an afternoon one
           * are two claims, and the pop-over is the first surface that can say which.
           *
           * ⚠️ Item 17's range tools are untouched — they act on the CELL, which is exactly what
           * the cell still is.
           */
          labels={{
            table: t('logs.timesheet.table'),
            task: t('time.column.task'),
            total: t('logs.timesheet.total'),
            dailyTotal: t('logs.timesheet.daily'),
            addTask: t('logs.timesheet.addTask'),
            addTaskSearch: t('logs.timesheet.addTaskSearch'),
            cell: (task, day) => t('logs.timesheet.cell', { task, day }),
            cellEntries: (task, day) => t('logs.timesheet.cellEntries', { task, day }),
            removeAlloc: (label) => t('logs.timesheet.removeAlloc', { label }),
            removeRow: (task) => t('logs.timesheet.removeRow', { task }),
            hoursHelp: t('time.hours.help'),
            empty: t('logs.timesheet.empty'),
            /* ⚠️ Given, so the tools are drawn — `timesheet.tsx` omits them for every other
               caller precisely by leaving these undefined. */
            copyOnwards: t('logs.timesheet.copyOnwards'),
            clearRange: t('logs.timesheet.clearRange'),
          }}
        />
      )}

      {/**
       * ⚠️⚠️ **OUTSIDE THE `view === 'timesheet'` BRANCH, DELIBERATELY.** A surface mounted
       * inside the branch that opens it unmounts the moment anything changes the view under it —
       * and one that vanishes mid-edit loses whatever was typed with no way to ask why. It is
       * closed by `openCell === null`, which is one state rather than two.
       */}
      {openCell !== null && (
        <CellPopover
          anchor={cellAnchor}
          taskKey={openCell.rowKey}
          taskTitle={
            weekEntries.find((entry) => entry.taskKey === openCell.rowKey)?.taskTitle ??
            openCell.rowKey
          }
          /* ⚠️ The COLUMN's own label, not the wire date — round 4 item 18 objected to seeing
             `2026-08-17` on a button, and the grid the reader just clicked says *aug. 17., hétfő*
             right above the cell. */
          dayLabel={timesheet.cols.find((col) => col.d === openCell.iso)?.label ?? openCell.iso}
          entries={weekEntries.filter(
            (entry) => entry.taskKey === openCell.rowKey && entry.workedOn === openCell.iso,
          )}
          canEdit={canEdit}
          canLog={canLog}
          lock={entryLock(t, openCell.iso, props.rules, props.mayOverride)}
          onEditHours={props.onEditHours}
          onEditNote={props.onEditNote}
          onDelete={props.onDelete}
          onMarkOvertime={props.onMarkOvertime}
          onAdd={(hours, note) => {
            /* ⚠️ The row key is the TASK KEY — the write needs the id, resolved the same way
               `onSet` resolves it, from the week's own entries or from an added row. */
            const task =
              weekEntries.find((entry) => entry.taskKey === openCell.rowKey) ??
              addableTasks.find((candidate) => candidate.key === openCell.rowKey);
            const taskId = task === undefined ? '' : 'taskId' in task ? task.taskId : task.id;
            if (taskId !== '') props.onLogAt(taskId, openCell.iso, hours, note);
          }}
          onClose={() => {
            setOpenCell(null);
          }}
        />
      )}

      {view === 'calendar' && (
        <LogsCalendar
          days={days}
          entries={weekEntries}
          today={today}
          canLog={canLog}
          onCreate={props.onCreateRange}
          onOpen={props.onOpenEntry}
        />
      )}

      {/* ⚠️ 31 Oct 2026 · the week's pager, over TASKS — the timesheet's rows, and the calendar
          draws the same page's hours. The strip the list and the grid draw, so the footers match. */}
      {weekly && weekPage !== undefined && !(view === 'timesheet' && portrait) ? (
        <div className="bg-muted/30 text-muted-foreground flex shrink-0 flex-wrap items-center gap-2 border-t px-4 py-2 text-[11.5px]">
          <div className="flex-1" />
          <ListPager
            page={weekPage}
            labels={{
              previousPage: t('list.previousPage'),
              nextPage: t('list.nextPage'),
              pageSelector: t('list.pageSelector'),
              pageOf: (current, count) => t('list.pageOf', { page: current, count }),
            }}
          />
        </div>
      ) : null}

      {view === 'grid' && (
        <div className="min-h-0 flex-1">
          <DataGrid
            columns={gridColumns}
            rows={gridRows}
            isLoading={loading}
            onChange={(rowId, key, value) => {
              const entry = entries.find((candidate) => candidate.id === rowId);
              if (entry === undefined) return;
              if (key === 'hours') props.onEditHours(entry, value);
              else if (key === 'note') props.onEditNote(entry, value);
              else if (key === 'day') props.onEditDay(entry, value);
            }}
            onBlocked={props.onBlocked}
            /* ⚠️ **Item 43 · the grid pages like the list.** It drew whatever the list request had
               returned and stopped there, so an hour past the page was unreachable and nothing on
               screen said so. Same `page`, so the two views cannot disagree about where you are. */
            {...(page === undefined ? {} : { page })}
            labels={{
              grid: t('logs.grid.table'),
              loading: t('state.loading'),
              readOnly: (label: string) => t('tasks.grid.readOnly', { label }),
              required: (label: string) => t('tasks.grid.required', { label }),
              /* ⚠️ The pager's words, from the same catalogue the list's come from. */
              previousPage: t('list.previousPage'),
              nextPage: t('list.nextPage'),
              pageSelector: t('list.pageSelector'),
              pageOf: (current: number, count: number) =>
                t('list.pageOf', { page: current, count }),
              pageTotal: (total: number) => t('list.pageTotal', { total }),
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * The timesheet before its first answer: a task column, seven days and a total, as placeholders —
 * its own empty row and `0:00` totals would read as a week with nothing logged.
 */
function TimesheetSkeleton() {
  const t = useT();
  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <div role="status" aria-live="polite" className="sr-only">
        {t('state.loading')}
      </div>
      <div aria-hidden>
        <div className="bg-muted/40 h-[34px] border-b" />
        {SKELETON_WIDTHS.slice(0, 6).map((_widths, index) => (
          <div key={index} className="flex h-[34px] items-center border-b">
            <div className="w-[260px] shrink-0 border-r px-2">
              <Skeleton width={skeletonWidth(index, 0)} />
            </div>
            {Array.from({ length: 8 }, (_, day) => (
              <div key={day} className="flex-1 px-2">
                <Skeleton width={skeletonWidth(index, day + 1) / 2} className="ml-auto" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * `2026-08-17`–`2026-08-23` → `aug. 17–23.` / `17–23 Aug`.
 *
 * ⚠️ **Wire dates are what the server speaks, not what anybody reads** — feedback round 4 item 18
 * objected to seeing them on the button. `toLocaleDateString` with the document's own locale rather
 * than a month table: `time-screen.tsx` records why a second month list is a second thing to get
 * wrong, and the mockup carries one only because it has no locale.
 *
 * ⚠️ **The month is said once when the week does not cross one**, which is six weeks in seven.
 */
function weekLabel(from: string, to: string, locale: string): string {
  const start = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  const month = (date: Date): string =>
    date.toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' });
  const day = (date: Date): string => String(date.getUTCDate());

  return month(start) === month(end)
    ? `${month(start)} ${day(start)}–${day(end)}.`
    : `${month(start)} ${day(start)}. – ${month(end)} ${day(end)}.`;
}
